import { Box, Flex, chakra, list, GridItem, Grid, Text, Divider } from "@chakra-ui/react";
import { KeyboardEventHandler, ReactNode, useCallback, useRef, useState } from "react";
import CardBoardCard from "./CardBoardCard";
import { Search } from "@/components/Search";
import getScrollBarProps from "@/styles/scrollBarProps";
import { useTranslate } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";

export interface ListData<T> {
  data: T;
  title: string;
  src: string;
  key: string;
  desc?: string;
}

export interface CardBoardProps<T> {
  list: ListData<T>[];
  selected?: ListData<T>;
  search?: boolean;
  searchFilter?(data: ListData<T>, searchKey: string): boolean;
  rightHeader?: ReactNode | string;
  cardBtnClick(cardData: ListData<T>): void;
  cardClick?(cardData: ListData<T>): void;
}

function CardBoard<T>(props: CardBoardProps<T>) {
  const {
    list,
    search,
    selected,
    searchFilter = () => true,
    rightHeader = "",
    cardBtnClick,
    cardClick = () => null,
  } = props;
  const { translate } = useTranslate();
  const [searchResult, setSearchResult] = useState(list);
  const searchRef = useRef<HTMLInputElement>(null);
  function handleSearch(key?: string) {
    if (!key) {
      setSearchResult(list);
    } else {
      setSearchResult(list.filter((data) => searchFilter(data, key)));
    }
    setTimeout(() => {
      searchRef.current?.focus();
    }, 0);
  }

  return (
    <Box bg="bg.secondary" px="35px" rounded="6px" h="100%">
      <Flex h="60px" align="center" justify="space-between">
        <Flex>
          {search && (
            <>
              <Text
                fontSize="16px"
                lineHeight="28px"
                color="text.secondary"
                mr="24px"
                fontWeight={600}
              >
                {translate(Editor_Words["cardboard-search"])}
              </Text>
              <Search
                ref={searchRef}
                h="28px"
                w="250px"
                placeholder={translate(Editor_Words["cardboard-search-placeholder"])}
                onSearch={handleSearch}
              />
            </>
          )}
        </Flex>
        <Text color="text.secondary">{rightHeader}</Text>
      </Flex>
      <Divider borderColor="text.secondary" mb="13px" />
      <Flex
        flexWrap="wrap"
        maxH="calc(100% - 74px)"
        overflowY="auto"
        {...getScrollBarProps({
          scrollBarColor: "#5f5f5f",
          scrollBarWidth: 4,
          scrollBarBorderRadius: 999,
          autoMargin: false,
        })}
      >
        {searchResult.map((card) => {
          return (
            <CardBoardCard
              mb="16px"
              mx="6px"
              btnSelectedText={translate(Editor_Words["using-keyword"])}
              flexShrink={0}
              key={card.key}
              src={card.src}
              title={card.title}
              desc={card.desc}
              btnText={translate(Editor_Words["use-keyword"])}
              selected={card.key === selected?.key}
              onBtnClick={() => cardBtnClick(card)}
              onClick={() => cardClick(card)}
            />
          );
        })}
      </Flex>
    </Box>
  );
}

export default chakra(CardBoard);
